import { ButtonGhostV2, TooltipV2 } from "oolib";
import { ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import React, { useLayoutEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { extractAllBlocksFromTpl } from "@okf/ootils";
import { getWarningConfig } from "@/_self_managed/utils/fieldTypeChangeWarningConfig";
import { fieldTypeTooltips } from "@/_self_managed/DirectDataImport/tooltipConfig";
import { FieldTypeChangeWarningModal } from "./comps/FieldTypeChangeWarningModal";
import TableDirectImportFieldSelector from "@/components/cards/TableElem/CONFIG_PARSER/TableCellBlocks/TableDirectImportFieldSelector";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useChangeFieldComp } from "@/utils/react-query-hooks/tci";
import { useDirectImportCompOptions } from "@/_self_managed/utils/useDirectImportCompOptions";
import { deriveFieldTypeKey } from "@/_self_managed/DirectDataImport/textCompDisplay";
import {
  SWAPPABLE_FIELD_COMPS_SET,
  getFieldTypeChangeDataLoss,
} from "@/_self_managed/utils/swappableFieldComps";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import { getTenant } from "@/TENANT_CONFIGS";

/**
 * SM-only "change field type" control on a listing-table column header — the UI
 * for the standalone PATCH /api/tpl/changeFieldComp endpoint. Gated to SM tenants upstream
 * (TableHeaderElem only renders it when custom_listingPageChangeFieldCompProps is passed, which
 * TableStyle sets only when SELF_MANAGED_BASE_CONFIGS is present).
 *
 * A pick is NOT the action: it parks in `pendingComp` until the user hits
 * Confirm, and a pick that would destroy data goes through the red warning modal
 * first (see getFieldTypeChangeDataLoss for which conversions those are).
 *
 * The panel is TableDirectImportFieldSelector, with the pencil as its trigger
 * and narrowed via `controlsToShow` to just the "Field type" section — the rest of that
 * panel is import-config that has nowhere to write to from a listing table.
 *
 * Swap targets come from compsAvailableForDirectDataImport (see
 * useDirectImportCompOptions), narrowed to SWAPPABLE_FIELD_COMPS — the same
 * list the reimport flow offers on an existing template field.
 */

// Hidden at rest by fading out rather than unmounting — same mechanism as
// ColumnSortDropdown's wrapper, so the pencil holds its layout slot instead of
// making the header text reflow as it appears and disappears on hover.
const StyledButtonWrapper = styled.div`
  display: inline-flex;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;

  button {
    padding: 0.4rem;
  }
`;

/**
 * Find the current comp of this column by its valuePath (e.g. "main.age") off
 * the tpl in the allTpls cache.
 */
const getStoredBlock = ({ allTpls, contentType, valuePath }) => {
  const tpl = (allTpls || []).find((t) => t.kp_content_type === contentType);
  if (!tpl) return undefined;
  return (extractAllBlocksFromTpl({ tpl }) || []).find(
    (b) => b.valuePath === valuePath
  );
};

export const ChangeFieldCompDropdown = ({
  colData,
  contentType,
  isHovered,
  // Derived by TableStyle's useFieldMigrationColumnState (single hook
  // instance) and threaded down: true while ANY of this contentType's columns
  // is anywhere in the type-change/migration window — mirrors the BE's
  // migration quiescence guard, which would 409 a concurrent type change.
  fieldTypeChangeDisabled,
}) => {
  const allTpls = useGetQueryData("allTpls");
  const changeFieldComp = useChangeFieldComp();
  const changeInProgress = changeFieldComp.isLoading || fieldTypeChangeDisabled;

  // Gated via FEATURES_DISABLED_ON_PROD ('listingFieldTypeChange') rather than a
  // bare VITE_ENV check, so this can be opened up per tenant with a
  // tenantWhitelist entry — matching how the reimport flow gates the same
  // capability ('reimportFieldTypeChange').
  const platformConfigs = useGetQueryData("platformConfigs");
  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } =
    platformConfigs || {};
  const fieldTypeChangeDisabledOnProd = isFeatureDisabledOnProd(
    "listingFieldTypeChange",
    {
      disabledFeatures,
      isProd: import.meta.env.VITE_ENV === "prod",
      tenant: getTenant(),
    }
  );

  // valuePath is the data accessor / API target; colId is opaque column
  // identity (stable across type changes) — never use one for the other.
  const valuePath = colData?.valuePath;
  const colId = colData?.colId;
  const storedBlock = getStoredBlock({ allTpls, contentType, valuePath });
  const storedComp = storedBlock?.comp;
  // Props matter for display only: Short Text and Long Text are both
  // LexicalTextEditor, told apart by props.shortText.
  const storedFieldItem = useMemo(
    () => (storedComp ? { comp: storedComp, props: storedBlock?.props } : undefined),
    [storedComp, storedBlock]
  );

  // The import flow's options, narrowed to the comps the BE will accept as a
  // swap target. Its "Document Title" pseudo-option isn't requested here, and
  // wouldn't survive this filter anyway.
  const allCompOptions = useDirectImportCompOptions();
  const compOptions = useMemo(() => {
    const swappable = allCompOptions.filter((o) =>
      SWAPPABLE_FIELD_COMPS_SET.has(o.comp)
    );
    // Short Text and Long Text share comp LexicalTextEditor, but this endpoint
    // takes a comp and nothing else — it can't carry the Short Text props
    // preset, and a same-comp change is a BE no-op (TYPE_UNCHANGED). So offer
    // exactly one Lexical option: the field's own form when it's already
    // Lexical, otherwise Long Text (the only form a bare comp swap produces).
    const currentKey = deriveFieldTypeKey(storedFieldItem, swappable);
    const keptLexicalKey =
      storedComp === "LexicalTextEditor" ? currentKey : "longText";
    return swappable.filter(
      (o) => o.comp !== "LexicalTextEditor" || o.key === keptLexicalKey
    );
  }, [allCompOptions, storedFieldItem, storedComp]);

  // The pick, held until Confirm. Undefined = nothing picked this session.
  const [pendingComp, setPendingComp] = useState(undefined);
  // The destructive pick awaiting the red modal's verdict. Carries its own
  // toComp rather than reading pendingComp, so the modal survives whatever
  // happens to the (now closed) panel's state behind it.
  const [warning, setWarning] = useState(undefined);

  // Flag every option that would destroy data. Per-option `iconAfter` puts the
  // warning in the option row's right-hand slot, leaving the field-type icon on
  // the left where it belongs — and it rides on the option object, so it reaches
  // oolib's ListItem without TableDirectImportFieldSelector having to forward
  // anything.
  const optionsWithLossWarnings = useMemo(
    () =>
      compOptions.map((option) => {
        const dataLoss = getFieldTypeChangeDataLoss({
          fromComp: storedComp,
          toComp: option.comp,
        });
        if (!dataLoss) return option;

        const { markerColor, markerTooltip } = getWarningConfig(
          dataLoss.severity
        );
        return {
          ...option,
          iconAfter: "WarningDiamond",
          iconAfterColor: markerColor,
          // Why it's flagged, after the field type's own description.
          // Keyed by field-type KEY, not comp — Short Text and Long Text share
          // comp LexicalTextEditor, so tooltipConfig is keyed the same way the
          // options are.
          tooltip: [fieldTypeTooltips[option.key], markerTooltip]
            .filter(Boolean)
            .join(" "),
        };
      }),
    [compOptions, storedComp]
  );

  // Anchor the panel to the whole column header cell rather than to the pencil,
  // so it sits centred under the column instead of hanging off the icon at the
  // cell's right edge. Found by query, as useResizeDataTableColumns already
  // does for the same colId.
  //
  // The id filter is load-bearing: TableCell tags every ROW cell with the same
  // data-col-id, so data-col-id alone would match whichever cell happens to
  // come first in the DOM.
  //
  // Left undefined until found — the Lightbox only overrides its reference when
  // anchorEl !== undefined, so until then it simply anchors to the trigger.
  const [anchorEl, setAnchorEl] = useState(undefined);
  useLayoutEffect(() => {
    if (!colId) return;
    // Our own cell is already committed by the time layout effects run.
    const cell = document.querySelector(
      `#TableHeaderCell[data-col-id="${CSS.escape(colId)}"]`
    );
    if (cell) setAnchorEl(cell);
  }, [colId]);

  // Hidden on prod: there's a confirm step now, but the swap still orphans the
  // column's data under the old valuePath and there is no undo. FE-only gate —
  // it hides this control, it does NOT disable the feature. PATCH
  // /api/tpl/changeFieldComp stays live on prod and still performs the swap for
  // anyone who calls it.
  if (fieldTypeChangeDisabledOnProd) return null;

  // No comp allowlist here — only the basics needed to render at all: we must
  // know which field this is, and its current type (to preselect it, and to
  // no-op a pick of the same type).
  if (!contentType || !valuePath || !storedComp) {
    return null;
  }

  if (storedComp === "TitleInput") {
    return (
      <StyledButtonWrapper isVisible={isHovered} onClick={(e) => e.stopPropagation()}>
        <TooltipV2 text="Field type of Title cannot be changed" placement="bottom">
          <ButtonGhostV2 icon="PencilSimple" S disabled />
        </TooltipV2>
      </StyledButtonWrapper>
    );
  }

  const displayOf = (comp) =>
    compOptions.find((o) => o.comp === comp)?.display || comp;

  // Nothing to orchestrate here: useFieldMigrationColumnState derives the
  // column's "Updating data…" state straight from this mutation's lifecycle
  // (loading → hold; error → revert; success → hold until the migration is
  // terminal per the socket tracker).
  const applyFieldTypeChange = (newComp) => {
    setPendingComp(undefined);
    setWarning(undefined);
    changeFieldComp.mutate({
      contentType,
      fieldCompChanges: [{ targetValuePath: valuePath, newComp }],
    });
  };

  const handleConfirm = ({ closePanel }) => {
    const dataLoss = getFieldTypeChangeDataLoss({
      fromComp: storedComp,
      toComp: pendingComp,
    });
    // The panel goes down either way — on a safe change because it's done, and
    // on a destructive one so the modal doesn't have to stack over the picker's
    // Lightbox and fight its click-outside dismissal.
    closePanel();
    if (dataLoss) {
      setWarning({ severity: dataLoss.severity, toComp: pendingComp });
      return;
    }
    applyFieldTypeChange(pendingComp);
  };

  return (

    <>
      <TableDirectImportFieldSelector
        // A bare { comp } can't say Short vs Long, so re-picking the field's
        // own type would resolve to Long Text and match no offered option,
        // blanking the selector until dismiss. Same comp => keep the stored
        // item, props and all.
        value={
          pendingComp && pendingComp !== storedComp
            ? { comp: pendingComp }
            : storedFieldItem
        }
        options={optionsWithLossWarnings}
        // Only the field-type dropdown is rendered, and it's the only control that
        // emits a `comp`. Guarding on it anyway keeps this a no-op rather than a
        // bad PATCH if `controlsToShow` ever widens.
        onChange={(_, v) => v.comp && setPendingComp(v.comp)}
        controlsToShow={["changeFieldComp"]}
        anchorEl={anchorEl}
        // Dismissing or reopening the panel discards an un-confirmed pick, so a
        // stale one can never be resurrected on the next open.
        onOpenChange={() => setPendingComp(undefined)}
        disabled={changeInProgress}
        // The panel heads itself with the column it's editing
        currentFieldName={colData?.headerDisplay}
        renderFieldTypeFooter={({ closePanel }) => (
          <ButtonPrimary
            S
            width="100%"
            disabled={!pendingComp || pendingComp === storedComp}
            onClick={() => handleConfirm({ closePanel })}
          >
            Confirm
          </ButtonPrimary>
        )}
      >
        {({ open }) => (
          <StyledButtonWrapper
            isVisible={open || isHovered}
            onClick={(e) => e.stopPropagation()}
          >
            <ButtonGhostV2
              icon="PencilSimple"
              S
              disabled={changeInProgress}
            />
          </StyledButtonWrapper>
        )}
      </TableDirectImportFieldSelector>
      {warning && (
        <FieldTypeChangeWarningModal
          config={{
            ...getWarningConfig(warning.severity),
            fieldLabel: colData?.headerDisplay || valuePath,
            fromDisplay: displayOf(storedComp),
            toDisplay: displayOf(warning.toComp),
          }}
          onConfirm={() => applyFieldTypeChange(warning.toComp)}
          onCancel={() => setWarning(undefined)}
        />
      )}
    </>
  );
};
